import { type FC, useEffect, useState } from 'react';

import { getIsPastBooking } from '@vakantiesnl/components/src/organisms/BookingDetails/helpers';
import { createLinks } from '@vakantiesnl/components/src/utils/contentfulMenuHelpers';
import { getEntryCollection } from '@vakantiesnl/services/src/contentful';
import { extendNavigationItems, mapMenuItem } from '@vakantiesnl/services/src/contentful/mapper';
import { useGetUserBookingDetails } from '@vakantiesnl/services/src/hooks/queries';
import { type FullUserBookingWithAccoDetails } from '@vakantiesnl/services/src/user';
import { errorHandler, parseErrorToReport } from '@vakantiesnl/services/src/util/errorHandling';
import { type RawElementNavItem, type ElementNavItem } from '@vakantiesnl/types';

import { BaseMenu } from './BaseMenu';

type Props = {
	bookingNumber: number;
};

export const SubMenu: FC<Props> = ({ bookingNumber }) => {
	const [subMenu, setSubMenu] = useState<ElementNavItem | undefined>(undefined);
	const { userBookingData } = useGetUserBookingDetails(bookingNumber);

	useEffect(() => {
		async function fetchSubMenu(bookingData: FullUserBookingWithAccoDetails): Promise<void> {
			try {
				const menuTitle = bookingData.isAccoOnly
					? 'Booking Information Menu AccoOnly'
					: 'Booking Information Menu';
				const rawSubMenu = await getEntryCollection('elementNavItem', {
					limit: 1,
					'fields.listTitle': menuTitle,
				});

				if (!rawSubMenu?.items?.length) {
					throw new Error(`Sub menu not found with title ${menuTitle}.`);
				}

				if ('listTitle' in rawSubMenu.items[0]?.fields) {
					const baseMenu = mapMenuItem(rawSubMenu.items[0] as unknown as RawElementNavItem);
					const menu = extendNavigationItems(baseMenu, bookingNumber.toString());

					const isPastOrCancelledBooking = getIsPastBooking(bookingData) || bookingData.isCancelled;

					if (isPastOrCancelledBooking) {
						const updatedMenu: ElementNavItem = {
							...menu,
							navigationItems: menu.navigationItems?.length ? [menu.navigationItems[0]] : [],
						};

						setSubMenu(updatedMenu);
					} else {
						setSubMenu(menu);
					}
				} else {
					throw new Error(`Sub menu has unexpected format with title ${menuTitle}.`);
				}
			} catch (err) {
				errorHandler.report(parseErrorToReport(err, 'Error fetching sub-menu from Contentful.'));
				// eslint-disable-next-line no-console
				console.error('Error fetching sub-menu from Contentful: ', err);
			}
		}

		userBookingData && fetchSubMenu(userBookingData);

		// Only refetch when bookingNumber changed and after userBookingData has been fetched for the first time
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bookingNumber, !!userBookingData]);

	const links = subMenu ? createLinks(subMenu) : [];

	return <BaseMenu links={links} />;
};
